import React from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import LocaleText from 'components/LocaleText'

export interface IViewToggleOption<T extends string = string> {
  id: T
  // Locale key rendered via <LocaleText>; omit for an icon-only tab.
  label?: string
  // Pass the icon WITHOUT a color prop — the toggle sets it (active/inactive) so lucide inherits currentColor.
  icon?: React.ReactNode
  ariaLabel?: string
}

export type ViewToggleSize = 'sm' | 'md'

interface IProps<T extends string = string> {
  options: IViewToggleOption<T>[]
  value: T
  onChange: (id: T) => void
  // 'md' — Library tokens (34px, 13px). 'sm' — dashboard tokens (26px, 12px).
  fullWidth?: boolean
  size?: ViewToggleSize
  activeColor?: string
  inactiveColor?: string
  containerBg?: string
}

const SIZES: Record<ViewToggleSize, Record<string, string>> = {
  md: {
    height: '34px',
    px: '14px',
    groupRadius: '10px',
    btnRadius: '8px',
    fontSize: '13px',
    innerGap: '7px',
    pad: '3px',
    groupGap: '3px',
  },
  sm: {
    height: '26px',
    px: '9px',
    groupRadius: '8px',
    btnRadius: '6px',
    fontSize: '12px',
    innerGap: '5px',
    pad: '2px',
    groupGap: '2px',
  },
}

const ACTIVE_SHADOW = '0 1px 3px rgba(15,23,42,0.10)'

// Shared #EEF0F2-pill segmented control with a white active tab. Renders any number of tabs from
// `options` (icon and/or label). Used by the Library grid/list toggle and the dashboard list/calendar toggle.
export function ViewToggle<T extends string = string>({
  options,
  value,
  onChange,
  size = 'md',
  fullWidth = false,
  activeColor = '#0F172A',
  inactiveColor = '#64748B',
  containerBg = '#EEF0F2',
}: IProps<T>) {
  const s = SIZES[size]
  return (
    <Flex
      flexShrink={0}
      width={fullWidth ? '100%' : undefined}
      align="center"
      bg={containerBg}
      borderRadius={s.groupRadius}
      p={s.pad}
      style={{ gap: s.groupGap }}
    >
      {options.map((opt) => {
        const isActive = value === opt.id
        const color = isActive ? activeColor : inactiveColor
        return (
          <Flex
            key={opt.id}
            as="button"
            {...{ type: 'button' }}
            aria-pressed={isActive}
            flex={fullWidth ? 1 : undefined}
            aria-label={opt.ariaLabel || opt.label}
            align="center"
            justify="center"
            height={s.height}
            px={s.px}
            borderRadius={s.btnRadius}
            bg={isActive ? 'white' : 'transparent'}
            boxShadow={isActive ? ACTIVE_SHADOW : 'none'}
            cursor="pointer"
            style={{ outline: 'none', border: 'none', transition: 'all 0.15s', gap: s.innerGap }}
            onClick={() => onChange(opt.id)}
          >
            {opt.icon && (
              <Box display="flex" alignItems="center" color={color}>
                {opt.icon}
              </Box>
            )}
            {opt.label && (
              <Text fontSize={s.fontSize} fontWeight={isActive ? 700 : 600} color={color}>
                <LocaleText text={opt.label} />
              </Text>
            )}
          </Flex>
        )
      })}
    </Flex>
  )
}
